import { execute } from '@lib/graphql/execute'
import { graphql } from '@lib/graphql/gql'

import { formatPhone } from '@lib/phone'
import { MutationConfig } from '@lib/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CountryCode, parsePhoneNumber } from 'libphonenumber-js/max'
import { z } from 'zod'
import { getCustomerQueryOptions } from './get-customer'

export const customerAddressUpdateFormSchema = z
  .object({
    firstName: z.string().min(1, { message: 'First name is required' }),
    lastName: z.string().min(1, { message: 'Last name is required' }),
    email: z.string().email().nullish(),
    phone: z.string().min(1, { message: 'Phone is required' }),
    phoneCountry: z.string({ message: 'Phone country is required' }),
    company: z.string().optional().nullable(),
    address1: z.string().min(1, { message: 'Address is required' }),
    address2: z.string().optional().nullable(),
    postalCode: z.string().min(1, { message: 'Postal code is required' }),
    city: z.string().min(1, { message: 'City is required' }),
    countryCode: z.string().min(1, { message: 'Country is required' }),
    state: z.string().optional().nullable(),
    isDefaultShipping: z.boolean(),
    isDefaultBilling: z.boolean(),
    metadata: z
      .array(
        z.object({
          key: z.string().min(1, { message: 'Key is required' }),
          value: z.string().optional().nullable(), // Optional and nullable
        }),
      )
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    try {
      const phoneNumberParsed = parsePhoneNumber(
        data.phone,
        data.phoneCountry as CountryCode,
      )
      if (!phoneNumberParsed.isValid()) throw phoneNumberParsed
    } catch {
      ctx.addIssue({
        path: ['phone'],
        message: 'Invalid phone number',
        code: 'custom',
      })
    }
  })

export type CustomerAddressUpdateFormValues = z.infer<
  typeof customerAddressUpdateFormSchema
>

const customerAddressUpdateMutationDocument = graphql(`
  mutation CustomerAddressUpdate($input: CustomerAddressUpdateInput!) {
    customerAddressUpdate(input: $input) {
      customerAddress {
        id
      }
      errors {
        type: __typename
      }
    }
  }
`)

export const customerAddressUpdate = ({
  addressId,
  data,
}: {
  addressId: string
  data: CustomerAddressUpdateFormValues
}) => {
  return execute(customerAddressUpdateMutationDocument, {
    input: {
      id: addressId,
      address: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        postalCode: data.postalCode,
        countryCode: data.countryCode,
        phone: formatPhone(data.phone, data.phoneCountry),
        company: data.company,
        state: data.state,
        isDefaultShipping: data.isDefaultShipping,
        isDefaultBilling: data.isDefaultBilling,
        metadata: data.metadata?.filter(
          (item): item is { key: string; value: string } => !!item.key,
        ),
      },
    },
  })
}

type UseCustomerAddressUpdateOptions = {
  mutationConfig?: MutationConfig<typeof customerAddressUpdate>
}

export const useCustomerAddressUpdate = ({
  mutationConfig,
}: UseCustomerAddressUpdateOptions = {}) => {
  const queryClient = useQueryClient()

  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    mutationFn: async (params: {
      addressId: string
      data: CustomerAddressUpdateFormValues
    }) => {
      // Step 1: Update the address
      const response = await customerAddressUpdate(params)

      return response
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: getCustomerQueryOptions().queryKey,
      })
      onSuccess?.(...args)
    },
    ...restConfig,
  })
}
